import * as React from "react";
import { useState, useEffect } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
import Link from "next/link";
import Button from "@src/components/Button";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

export function insertAt(
  array: unknown[],
  index: number,
  ...elementsArray: unknown[]
) {
  array.splice(index, 0, ...elementsArray);
}

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  const mediaData = React.useMemo(
    () => albumDetailsData?.getMedia || [],
    [albumDetailsData]
  );
  const [currentImageSet, setCurrentImageSet] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const isValidImageUrl = (img: any): boolean => {
    return (
      img &&
      img.meta &&
      typeof img.meta.s3_url === "string" &&
      img.meta.s3_url.trim() !== "" &&
      img.meta.s3_url.startsWith("http")
    );
  };

  const imageSets = React.useMemo(() => {
    const validMedia = mediaData.filter(isValidImageUrl);

    if (validMedia.length === 0) return [];

    if (validMedia.length === 5) {
      return [validMedia];
    }

    if (validMedia.length > 5) {
      const sets = [];
      const totalImages = validMedia.length;

      const numSets = Math.ceil(totalImages / 5);

      for (let setIndex = 0; setIndex < numSets; setIndex++) {
        const set = [];
        for (let positionIndex = 0; positionIndex < 5; positionIndex++) {
          const imageIndex = (setIndex * 5 + positionIndex) % totalImages;
          set.push(validMedia[imageIndex]);
        }
        sets.push(set);
      }

      return sets;
    }

    return [];
  }, [mediaData]);

  useEffect(() => {
    if (imageSets.length <= 1) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentImageSet((prev) => (prev + 1) % imageSets.length);
        setIsAnimating(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, [imageSets.length]);

  const handleImageSetChange = (index: number) => {
    if (index === currentImageSet || isAnimating) return;

    setIsAnimating(true);
    setTimeout(() => {
      setCurrentImageSet(index);
      setIsAnimating(false);
    }, 300);
  };

  const currentSet = imageSets[currentImageSet] || [];

  if (
    !albumDetailsData?.getMedia ||
    albumDetailsData.getMedia.length === 0 ||
    imageSets.length === 0 ||
    currentSet.length === 0
  ) {
    return null;
  }

  return (
    <div className="bg-white lg:pt-[8rem] pt-[5rem] pb-[3rem] px-4 relative">
      <div className="max-w-6xl mx-auto">
        {/* Large Screens */}
        <div className="relative w-full flex justify-center mb-8">
          <div
            className={`flex flex-row items-center transition-all duration-500 justify-center ${
              isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
            }`}
          >
            <div className="flex flex-col gap-4 justify-between items-end mr-[-3rem] z-10">
              {/* First Image */}
              {currentSet[0] && isValidImageUrl(currentSet[0]) && (
                <div className="relative w-full cursor-pointer transition-all duration-500 transform hover:scale-[1.02]">
                  <div
                    className="w-[10rem] sm:w-[20rem] h-[8rem] sm:h-[18rem] bg-white p-3 pb-6 flex-shrink-0"
                    style={{
                      boxShadow:
                        "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                    }}
                  >
                    <div className="relative w-full h-full rounded-sm overflow-hidden">
                      <Image
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(700, 475)
                        )}`}
                        src={currentSet[0]?.meta?.s3_url || ""}
                        layout="fill"
                        alt="gallery"
                        objectFit="cover"
                        className="object-center"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Second Image */}
              {currentSet[1] && isValidImageUrl(currentSet[1]) && (
                <div className="relative w-full cursor-pointer transition-all duration-500 transform hover:scale-[1.02]">
                  <div
                    className="w-[10rem] sm:w-[20rem] h-[8rem] sm:h-[18rem] bg-white p-3 pb-6 flex-shrink-0"
                    style={{
                      boxShadow:
                        "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                    }}
                  >
                    <div className="relative w-full h-full rounded-sm overflow-hidden">
                      <Image
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(700, 475)
                        )}`}
                        src={currentSet[1]?.meta?.s3_url || ""}
                        layout="fill"
                        alt="gallery"
                        objectFit="cover"
                        className="object-center"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-7">
              {/* Third Image */}
              {currentSet[2] && isValidImageUrl(currentSet[2]) && (
                <div className="relative w-full cursor-pointer transition-all duration-500 transform hover:scale-[1.02]">
                  <div
                    className="w-[10rem] sm:w-[20rem] h-[14rem] sm:h-[28rem] bg-white p-3 pb-6 flex-shrink-0"
                    style={{
                      boxShadow:
                        "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                    }}
                  >
                    <div className="relative w-full h-full rounded-sm overflow-hidden">
                      <Image
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(700, 475)
                        )}`}
                        src={currentSet[2]?.meta?.s3_url || ""}
                        layout="fill"
                        alt="gallery"
                        objectFit="cover"
                        className="object-center"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4 justify-between items-end ml-[-3rem]">
              {/* First Image */}
              {currentSet[0] && isValidImageUrl(currentSet[0]) && (
                <div className="relative w-full cursor-pointer transition-all duration-500 transform hover:scale-[1.02]">
                  <div
                    className="w-[10rem] sm:w-[20rem] h-[8rem] sm:h-[18rem] bg-white p-3 pb-6 flex-shrink-0"
                    style={{
                      boxShadow:
                        "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                    }}
                  >
                    <div className="relative w-full h-full rounded-sm overflow-hidden">
                      <Image
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(700, 475)
                        )}`}
                        src={currentSet[0]?.meta?.s3_url || ""}
                        layout="fill"
                        alt="gallery"
                        objectFit="cover"
                        className="object-center"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Second Image */}
              {currentSet[1] && isValidImageUrl(currentSet[1]) && (
                <div className="relative w-full cursor-pointer transition-all duration-500 transform hover:scale-[1.02]">
                  <div
                    className="w-[10rem] sm:w-[20rem] h-[8rem] sm:h-[18rem] bg-white p-3 pb-6 flex-shrink-0"
                    style={{
                      boxShadow:
                        "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                    }}
                  >
                    <div className="relative w-full h-full rounded-sm overflow-hidden">
                      <Image
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(700, 475)
                        )}`}
                        src={currentSet[1]?.meta?.s3_url || ""}
                        layout="fill"
                        alt="gallery"
                        objectFit="cover"
                        className="object-center"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Image set indicators */}
        {imageSets.length > 1 && (
          <div className="flex justify-center gap-3 mb-8">
            {imageSets.map((_, index) => (
              <button
                key={index}
                onClick={() => handleImageSetChange(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentImageSet
                    ? "bg-black scale-125 shadow-lg"
                    : "bg-black/40 hover:bg-black/70"
                }`}
                aria-label={`View image set ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Button */}
      {eventSlug && (
        <div className="flex flex-col md:flex-row gap-8 justify-center items-center">
          <Link
            href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
            passHref
          >
            <a target="_blank" rel="noopener noreferrer">
              <button
                className="border-[#8F2F33] font-inter border-2 hover:bg-[#8F2F33] text-black hover:text-white px-3 py-2 font-medium transition-colors text-sm"
                type="button"
              >
                See Full Photobook
              </button>
            </a>
          </Link>
        </div>
      )}

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${
              mediaData.length * 320 + (mediaData.length - 1) * 24
            }px);
          }
        }
        
        .animate-scroll {
          animation: scroll ${mediaData.length * 4}s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default Masonry;
